import React, { useState, useEffect } from "react";
import axios from "axios";
import { useParams, Link } from "react-router-dom";
import parse from "html-react-parser";
import { motion, AnimatePresence } from "framer-motion";
import { Calendar, User, ArrowLeft, ChevronLeft, ChevronRight, Share2, AlertCircle, Maximize2, X } from "lucide-react";

const BlogDetail = () => {
  const { idOrSlug } = useParams();
  const [blog, setBlog] = useState(null);
  const [recentBlogs, setRecentBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Carousel State
  const [currentSlide, setCurrentSlide] = useState(0);
  
  // Lightbox State
  const [activeLightbox, setActiveLightbox] = useState(null);

  useEffect(() => {
    const fetchBlogAndRecents = async () => {
      setLoading(true);
      setError(null);
      try {
        // Fetch current blog
        const res = await axios.get(`${window.API_URL}/blogs/${idOrSlug}`);
        setBlog(res.data);
        setCurrentSlide(0); // Reset slide on post change

        // Fetch all blogs to filter recents
        const listRes = await axios.get(window.API_URL + "/blogs");
        const listData = listRes.data || [];
        // Filter out current blog and take latest 3
        const recents = listData
          .filter((b) => b._id !== res.data._id)
          .slice(0, 3);
        setRecentBlogs(recents);
      } catch (err) {
        console.error("Error fetching blog details:", err);
        setError("We couldn't load this blog post. It may have been deleted or the link is incorrect.");
      } finally {
        setLoading(false);
      }
    };

    fetchBlogAndRecents();
  }, [idOrSlug]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[70vh]">
        <div className="w-12 h-12 border-4 border-[#f06600]/20 border-t-[#f06600] rounded-full animate-spin"></div>
        <p className="text-gray-500 mt-4 font-semibold">Loading article...</p>
      </div>
    );
  }

  if (error || !blog) {
    return (
      <div className="max-w-md mx-auto px-6 py-20 text-center">
        <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Failed to load blog post</h2>
        <p className="text-gray-500 mb-6 text-sm">{error || "Blog post not found."}</p>
        <Link to="/blog" className="px-6 py-3 bg-[#f06600] text-white hover:bg-[#d05800] rounded-xl font-bold transition shadow-md shadow-[#f06600]/20">
          Back to Blog List
        </Link>
      </div>
    );
  }

  const { title, content, images = [], layoutStyle = "carousel", author, tags = [], createdAt } = blog;

  // Carousel controls
  const nextSlide = () => {
    setCurrentSlide((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };
  const prevSlide = () => {
    setCurrentSlide((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  // Image Layout Renderers
  const renderCarousel = () => {
    if (images.length === 0) return null;
    return (
      <div className="relative group w-full h-[300px] sm:h-[450px] md:h-[550px] bg-black rounded-3xl overflow-hidden shadow-lg mb-10">
        <img
          src={`${window.API_URL}/uploads/${images[currentSlide]}`}
          alt={`${title} slide ${currentSlide + 1}`}
          className="w-full h-full object-cover transition-all duration-500"
          onClick={() => setActiveLightbox(images[currentSlide])}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-black/20 pointer-events-none"></div>

        {/* Hover Lightbox Indicator */}
        <div 
          onClick={() => setActiveLightbox(images[currentSlide])}
          className="absolute top-4 right-4 bg-black/60 hover:bg-black/80 text-white p-2 rounded-full cursor-pointer opacity-0 group-hover:opacity-100 transition shadow-lg"
          title="Open in lightbox"
        >
          <Maximize2 className="w-5 h-5" />
        </div>

        {/* Slide navigation controls */}
        {images.length > 1 && (
          <>
            <button
              onClick={prevSlide}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center rounded-full bg-white/90 hover:bg-white text-gray-900 shadow-md transition hover:scale-105"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button
              onClick={nextSlide}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center rounded-full bg-white/90 hover:bg-white text-gray-900 shadow-md transition hover:scale-105"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
            {/* Dots */}
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
              {images.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentSlide(idx)}
                  className={`w-2.5 h-2.5 rounded-full transition-all ${
                    currentSlide === idx ? "bg-[#f06600] w-6" : "bg-white/60 hover:bg-white"
                  }`}
                />
              ))}
            </div>
          </>
        )}
      </div>
    );
  };

  const renderGrid = () => {
    if (images.length === 0) return null;
    
    // Single image fallback
    if (images.length === 1) {
      return (
        <div 
          className="relative h-[300px] sm:h-[450px] md:h-[500px] rounded-3xl overflow-hidden shadow-md mb-10 cursor-pointer group"
          onClick={() => setActiveLightbox(images[0])}
        >
          <img src={`${window.API_URL}/uploads/${images[0]}`} alt={title} className="w-full h-full object-cover group-hover:scale-[1.01] transition-transform duration-500" />
          <div className="absolute top-4 right-4 bg-black/60 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition shadow">
            <Maximize2 className="w-4 h-4" />
          </div>
        </div>
      );
    }

    // Grid layout for 2 to 4 images
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10 h-auto md:h-[450px]">
        {/* Large cover left */}
        <div 
          className="md:col-span-2 relative rounded-3xl overflow-hidden bg-gray-100 border border-gray-100 shadow-sm cursor-pointer group h-[300px] md:h-full"
          onClick={() => setActiveLightbox(images[0])}
        >
          <img
            src={`${window.API_URL}/uploads/${images[0]}`}
            alt={`${title} grid 1`}
            className="w-full h-full object-cover group-hover:scale-[1.02] transition-all duration-500"
          />
          <div className="absolute top-4 right-4 bg-black/60 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition shadow">
            <Maximize2 className="w-4 h-4" />
          </div>
        </div>

        {/* Small stack right */}
        <div className="flex flex-row md:flex-col gap-4 h-[120px] md:h-full justify-between">
          {images.slice(1, 4).map((img, idx) => (
            <div
              key={idx}
              className="flex-1 relative rounded-2xl overflow-hidden bg-gray-100 border border-gray-100 shadow-sm cursor-pointer group h-full md:h-[31%]"
              onClick={() => setActiveLightbox(img)}
            >
              <img
                src={`${window.API_URL}/uploads/${img}`}
                alt={`${title} grid ${idx + 2}`}
                className="w-full h-full object-cover group-hover:scale-105 transition-all duration-300"
              />
              <div className="absolute top-2 right-2 bg-black/60 text-white p-1.5 rounded-full opacity-0 group-hover:opacity-100 transition shadow">
                <Maximize2 className="w-3.5 h-3.5" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderBottomGallery = () => {
    // If layout is bottom gallery, we show only the cover image at the top
    if (images.length === 0) return null;
    return (
      <div 
        className="relative h-[300px] sm:h-[450px] rounded-3xl overflow-hidden shadow-md mb-10 cursor-pointer group"
        onClick={() => setActiveLightbox(images[0])}
      >
        <img src={`${window.API_URL}/uploads/${images[0]}`} alt={title} className="w-full h-full object-cover group-hover:scale-[1.01] transition-transform duration-500" />
        <div className="absolute top-4 right-4 bg-black/60 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition shadow">
          <Maximize2 className="w-4 h-4" />
        </div>
      </div>
    );
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: title,
        url: window.location.href
      }).catch(err => console.error(err));
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert("Link copied to clipboard!");
    }
  };

  return (
    <div className="bg-gray-50 min-h-screen pb-20 font-sans text-gray-800 antialiased">
      {/* Navigation Header */}
      <div className="max-w-5xl mx-auto px-6 pt-8 pb-4">
        <Link
          to="/blog"
          className="inline-flex items-center gap-2 text-sm font-bold text-gray-500 hover:text-[#f06600] transition"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Articles
        </Link>
      </div>

      {/* Article Container */}
      <div className="max-w-5xl mx-auto px-6">
        {/* Dynamic Image Display Area */}
        {layoutStyle === "carousel" && renderCarousel()}
        {layoutStyle === "grid" && renderGrid()}
        {layoutStyle === "gallery" && renderBottomGallery()}

        {/* Content Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mt-8">
          {/* Main Article Block */}
          <article className="lg:col-span-8 bg-white p-6 md:p-10 rounded-3xl border border-gray-100 shadow-sm">
            {/* Tags */}
            {tags && tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4">
                {tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 bg-[#f06600]/10 border border-[#f06600]/25 text-[#f06600] text-xs font-bold rounded-lg capitalize"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}

            <h1 className="text-3xl md:text-4xl font-extrabold text-gray-900 leading-snug tracking-tight mb-6">
              {title}
            </h1>

            {/* Author and Date Meta info */}
            <div className="flex flex-wrap items-center justify-between gap-4 pb-6 border-b border-gray-100 mb-8">
              <div className="flex items-center gap-4 text-sm font-medium text-gray-500">
                <span className="flex items-center gap-1.5">
                  <User className="w-4 h-4 text-[#f06600]" /> By {author}
                </span>
                <span>•</span>
                <span className="flex items-center gap-1.5">
                  <Calendar className="w-4 h-4 text-[#f06600]" /> {new Date(createdAt).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
                </span>
              </div>
              <button
                onClick={handleShare}
                className="flex items-center gap-1.5 text-xs font-bold text-gray-700 bg-gray-100 hover:bg-[#f06600] hover:text-white px-3.5 py-2 rounded-xl transition"
              >
                <Share2 className="w-3.5 h-3.5" /> Share Article
              </button>
            </div>

            {/* Rendered HTML content from Quill */}
            <div className="prose prose-orange max-w-none text-gray-700 leading-relaxed space-y-6 text-base md:text-[17px] font-normal blog-content-view">
              {parse(content)}
            </div>

            {/* Masonry / Gallery display at bottom if layout set to gallery */}
            {layoutStyle === "gallery" && images.length > 1 && (
              <div className="mt-12 pt-8 border-t border-gray-100">
                <h3 className="text-xl font-bold text-gray-900 mb-6">Article Media Gallery</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {images.slice(1).map((img, idx) => (
                    <div
                      key={idx}
                      className="relative aspect-square rounded-2xl overflow-hidden border border-gray-100 shadow-sm cursor-pointer group bg-gray-100"
                      onClick={() => setActiveLightbox(img)}
                    >
                      <img
                        src={`${window.API_URL}/uploads/${img}`}
                        alt={`gallery-${idx}`}
                        className="w-full h-full object-cover transition duration-300 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 flex items-center justify-center transition">
                        <Maximize2 className="w-6 h-6 text-white" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </article>

          {/* Right Sidebar */}
          <aside className="lg:col-span-4 space-y-8">
            {/* Portal Banner / AD */}
            <div className="bg-gradient-to-br from-[#f06600] to-orange-500 rounded-3xl p-6 text-white shadow-lg shadow-orange-500/20 relative overflow-hidden">
              <div className="absolute -right-10 -bottom-10 w-36 h-36 bg-white/10 rounded-full blur-2xl"></div>
              <h4 className="text-xl font-extrabold mb-2">Build with Beta</h4>
              <p className="text-white/85 text-xs md:text-sm leading-relaxed mb-6">
                Are you looking to scale your engineering team or build custom web solutions? Let's collaborate.
              </p>
              <Link
                to="/contactus"
                className="inline-block bg-white text-[#f06600] font-bold text-xs px-5 py-3 rounded-xl shadow-md hover:scale-105 transition"
              >
                Connect With Us
              </Link>
            </div>

            {/* Recent Posts widget */}
            <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm">
              <h4 className="font-extrabold text-gray-900 text-lg mb-6 border-b pb-3 border-gray-50">Recent Articles</h4>
              {recentBlogs.length > 0 ? (
                <div className="space-y-6">
                  {recentBlogs.map((b) => (
                    <div key={b._id} className="flex gap-4 group">
                      <div className="w-16 h-16 rounded-xl bg-gray-100 overflow-hidden shrink-0 border border-gray-100">
                        {b.images && b.images.length > 0 ? (
                          <img src={`${window.API_URL}/uploads/${b.images[0]}`} alt={b.title} className="w-full h-full object-cover group-hover:scale-105 transition duration-300" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs">
                            Blog
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col justify-center">
                        <span className="text-[10px] text-gray-400 font-semibold">{new Date(b.createdAt).toLocaleDateString()}</span>
                        <h5 className="font-bold text-sm text-gray-900 line-clamp-2 leading-snug group-hover:text-[#f06600] transition duration-300 mt-1">
                          <Link to={`/blog/${b.slug}`}>{b.title}</Link>
                        </h5>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-400 text-xs">No other recent articles.</p>
              )}
            </div>
          </aside>
        </div>
      </div>

      {/* Lightbox Modal */}
      <AnimatePresence>
        {activeLightbox && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setActiveLightbox(null)}
            className="fixed inset-0 z-[2000] bg-black/95 backdrop-blur-sm flex items-center justify-center p-4 cursor-zoom-out"
          >
            <button
              onClick={() => setActiveLightbox(null)}
              className="absolute top-6 right-6 text-white/70 hover:text-white bg-white/10 hover:bg-white/20 p-2.5 rounded-full transition"
            >
              <X className="w-6 h-6" />
            </button>
            <motion.img
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              src={`${window.API_URL}/uploads/${activeLightbox}`}
              alt="Lightbox View"
              className="max-w-full max-h-[90vh] object-contain rounded-xl shadow-2xl"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default BlogDetail;
